import React from 'react';
import Select, { StylesConfig, GroupBase } from 'react-select';

interface OptionType {
  value: string;
  label: string;
  color?: string;
}

interface ReactSelectProps {
  options: OptionType[];
  value?: OptionType | null;
  onChange?: (selectedOption: OptionType | null) => void;
  placeholder?: string;
  isDisabled?: boolean;
  className?: string;
}

const customStyles: StylesConfig<OptionType, false, GroupBase<OptionType>> = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: 'white',
    borderColor: state.isFocused ? 'rgba(251, 146, 60, 0.7)' : 'rgba(254, 215, 170, 0.5)', // orange-400/70 or orange-200/50
    borderRadius: '0.75rem', // rounded-xl
    padding: '0.25rem', // Reduced padding to match standard input height
    minHeight: '48px', // Match standard input height
    height: '48px',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(251, 146, 60, 0.4)' : 'none', // ring-orange-400/40
    '&:hover': {
      borderColor: 'rgba(251, 146, 60, 0.7)',
    },
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: 'white',
    borderRadius: '0.75rem',
    border: '1px solid rgba(254, 215, 170, 0.5)', // orange-200/50
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    zIndex: 50,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? 'rgba(255, 237, 213, 0.8)' // orange-100/80
      : state.isFocused
        ? 'rgba(255, 247, 237, 0.8)' // orange-50/80
        : 'transparent',
    color: state.isSelected ? '#c2410c' : '#1f2937', // orange-700 or gray-800
    padding: '0.75rem 1rem',
    cursor: 'pointer',
    '&:active': {
      backgroundColor: 'rgba(255, 237, 213, 0.8)',
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#111827', // gray-900
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#9ca3af', // gray-400
  }),
  input: (provided) => ({
    ...provided,
    color: '#111827', // gray-900
    margin: 0,
    padding: 0,
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: '#9ca3af', // gray-400
    '&:hover': {
      color: '#4b5563', // gray-600
    },
  }),
  clearIndicator: (provided) => ({
    ...provided,
    color: '#9ca3af',
    '&:hover': {
      color: '#4b5563',
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '0 1rem',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
  }),
};

const ReactSelect: React.FC<ReactSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  isDisabled = false,
  className = '',
}) => {
  const formatOptionLabel = ({ label, color }: OptionType) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      {color && (
        <span
          style={{
            backgroundColor: color,
            width: '0.75rem',
            height: '0.75rem',
            borderRadius: '50%',
            display: 'inline-block',
            flexShrink: 0,
          }}
        />
      )}
      <span>{label}</span>
    </div>
  );

  return (
    <Select<OptionType>
      options={options}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      isDisabled={isDisabled}
      styles={customStyles}
      className={className}
      classNamePrefix="react-select"
      formatOptionLabel={formatOptionLabel}
      theme={(theme) => ({
        ...theme,
        colors: {
          ...theme.colors,
          primary: 'rgb(255, 89, 36)', // orange-500
          primary75: 'rgba(139, 92, 246, 0.75)',
          primary50: 'rgba(139, 92, 246, 0.5)',
          primary25: 'rgba(139, 92, 246, 0.25)',
        },
      })}
    />
  );
};

export default ReactSelect;
export type { OptionType };

